import {
  APPOINTMENT_STATUS_LABELS,
  type AppointmentStatusValue,
  addDays,
  endOfDay,
  formatTime,
  fromDateInput,
  startOfDay,
  toDateInput,
} from '@crm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  cn,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
} from '@crm/ui';
import {
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Hourglass,
  IdCard,
  LogIn,
  LogOut,
  NotebookPen,
  PlayCircle,
  SlidersHorizontal,
  UserX,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLayoutStore, useOpenSection } from '@/app/layouts';
import {
  Autocomplete,
  type AutocompleteOption,
  Avatar,
  CardGrid,
  ConfirmDialog,
  PageHeader,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import { formatCurrency } from '@/lib/utils';
import { useCan, useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { CustomerForm } from '@/modules/customers/components/customer-form';
import { PaymentSheet } from '@/modules/receivables/components/payment-sheet';
import {
  type Appointment,
  SCHEDULE_ROUTES,
  useAgenda,
  useAppointmentAction,
  useProfessionals,
  useSaveNotes,
} from '../api';
import { OperationTabs } from '../components/operation-tabs';
import { longDayLabel, shortDayLabel } from '../date-labels';

/** Um ponto colorido diz o estado sem competir com o nome do cliente. */
const STATUS_DOT: Record<string, string> = {
  SCHEDULED: 'bg-muted-foreground/40',
  CONFIRMED: 'bg-primary',
  IN_PROGRESS: 'bg-amber-500',
  COMPLETED: 'bg-emerald-500',
  CANCELLED: 'bg-destructive',
  NO_SHOW: 'bg-destructive',
};

/** As abas sao o corte por momento do atendimento. */
type TabKey = 'queue' | 'running' | 'done' | 'noShow' | 'cancelled';

const TABS: {
  key: TabKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  statuses: AppointmentStatusValue[];
}[] = [
  { key: 'queue', label: 'Fila', icon: Hourglass, statuses: ['SCHEDULED', 'CONFIRMED'] },
  { key: 'running', label: 'Em andamento', icon: PlayCircle, statuses: ['IN_PROGRESS'] },
  { key: 'done', label: 'Finalizados', icon: CheckCircle2, statuses: ['COMPLETED'] },
  // Quem nao apareceu e quem desmarcou sao coisas diferentes para o salao:
  // uma vira horario perdido, a outra e agenda liberada a tempo.
  { key: 'noShow', label: 'Desistencias', icon: UserX, statuses: ['NO_SHOW'] },
  { key: 'cancelled', label: 'Cancelados', icon: XCircle, statuses: ['CANCELLED'] },
];

/** As duas acoes que pedem confirmacao; o encerramento pede o pagamento. */
type ConfirmMode = 'checkIn' | 'noShow';

const CONFIRM_COPY: Record<ConfirmMode, { title: string; label: string; message: string }> = {
  checkIn: {
    title: 'Iniciar atendimento',
    label: 'Fazer check-in',
    message: 'O horario passa a contar como em atendimento a partir de agora.',
  },
  noShow: {
    title: 'Marcar desistencia',
    label: 'Marcar desistencia',
    message: 'O horario sera fechado como desistencia e liberado na agenda.',
  },
};

/** Anotacoes do atendimento: nascem baixinhas e crescem conforme se digita. */
function NotesField({ appointment, disabled }: { appointment: Appointment; disabled: boolean }) {
  const saved = appointment.notes ?? '';
  const [isOpen, setOpen] = useState(false);
  const [notes, setNotes] = useState(saved);
  const saveNotes = useSaveNotes();

  return (
    <>
      {/* No cartao fica so a linha da anotacao: escrever e coisa de modal, com
          cancelar e confirmar, para nao salvar sem querer no meio da fila. */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setNotes(saved);
          setOpen(true);
        }}
        className="flex w-full items-start gap-2 text-left text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
      >
        <NotebookPen className="mt-0.5 h-4 w-4 shrink-0" />
        <span className="line-clamp-2">{saved || 'Anotar'}</span>
      </button>

      <Modal
        open={isOpen}
        onClose={() => setOpen(false)}
        title="Anotacoes do atendimento"
        subtitle={appointment.customerName}
        size="medium"
        actions={[
          { label: 'Cancelar', variant: 'outline', onClick: () => setOpen(false) },
          {
            label: 'Confirmar',
            loading: saveNotes.isPending,
            onClick: () =>
              saveNotes.mutate({ id: appointment.id, notes }, { onSuccess: () => setOpen(false) }),
          },
        ]}
      >
        <Textarea
          rows={6}
          autoFocus
          disabled={disabled}
          placeholder="O que precisa ficar registrado deste atendimento"
          className="text-base"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </Modal>
    </>
  );
}

/**
 * Fila do dia. Cada atendimento e um cartao curto - quem, o que e quanto - com
 * as anotacoes logo abaixo e as acoes num rodape, que vale para tudo acima.
 */
export function ServiceDeskPage() {
  useDocumentTitle('Checkin de atendimentos');

  const canWrite = useCan('appointments', 'WRITE');
  const { user } = useSession();
  const units = user?.units ?? [];
  const { closeAfterNavigate } = useOpenSection();
  const setFullWidth = useLayoutStore((state) => state.setFullWidth);

  // Mesma moldura da agenda: largura cheia e o fundo da operacao.
  useEffect(() => {
    setFullWidth(true);

    return () => setFullWidth(false);
  }, [setFullWidth]);

  const today = startOfDay(new Date());
  const [from, setFrom] = useState(() => toDateInput(today));
  const [to, setTo] = useState(() => toDateInput(today));
  const [unitId, setUnitId] = useState('');
  /** A aba faz o papel do filtro de status - e o corte que mais se usa. */
  const [tab, setTab] = useState<TabKey>('queue');
  const [professionalId, setProfessionalId] = useState('');
  const [customer, setCustomer] = useState<AutocompleteOption[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const [confirm, setConfirm] = useState<{ mode: ConfirmMode; appointment: Appointment } | null>(
    null,
  );
  const [pendingPayment, setPendingPayment] = useState<Appointment | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Appointment | null>(null);

  const professionals = useProfessionals(unitId || undefined);
  const agenda = useAgenda({
    from: fromDateInput(from),
    to: endOfDay(fromDateInput(to)),
    unitId: unitId || undefined,
    professionalId: professionalId || undefined,
    customerId: customer[0]?.id,
  });
  const action = useAppointmentAction();

  const appointments = agenda.data?.data ?? [];

  /** Quantos ha em cada aba - o numero fica na propria aba. */
  const byTab = new Map<TabKey, Appointment[]>(
    TABS.map((entry) => [
      entry.key,
      appointments.filter((item) => entry.statuses.includes(item.status)),
    ]),
  );
  const visible = byTab.get(tab) ?? [];

  const summary = [
    {
      label: 'Na fila',
      value: String((byTab.get('queue') ?? []).length),
      icon: <Hourglass className="h-5 w-5" />,
      tone: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
    },
    {
      label: 'Em andamento',
      value: String((byTab.get('running') ?? []).length),
      icon: <PlayCircle className="h-5 w-5" />,
      tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    },
    {
      label: 'Encerrados',
      value: String((byTab.get('done') ?? []).length),
      icon: <CheckCircle2 className="h-5 w-5" />,
      tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    },
    {
      label: 'Desistencias',
      value: String((byTab.get('noShow') ?? []).length),
      icon: <UserX className="h-5 w-5" />,
      tone: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
    },
    {
      label: 'Cancelados',
      value: String((byTab.get('cancelled') ?? []).length),
      icon: <XCircle className="h-5 w-5" />,
      tone: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
    },
    {
      label: 'Remarcados',
      value: String(appointments.filter((item) => item.rescheduledAt !== null).length),
      icon: <CalendarDays className="h-5 w-5" />,
      tone: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
    },
  ];

  const isSingleDay = from === to;

  function moveDay(direction: 1 | -1) {
    const next = toDateInput(addDays(fromDateInput(from), direction));
    setFrom(next);
    setTo(next);
  }

  function card(appointment: Appointment) {
    const canStart = appointment.status === 'SCHEDULED' || appointment.status === 'CONFIRMED';
    const canFinish = appointment.status === 'IN_PROGRESS';
    const isClosed = !canStart && !canFinish;

    return (
      <Card
        key={appointment.id}
        className={cn(
          // Mesmo cartao dos widgets e do resumo do agendamento: canto quase
          // reto, contorno marcado e uma sombra curta que o levanta do cinza.
          // Em coluna e com altura cheia: numa fileira em que um cartao tem
          // servico de duas linhas, os vizinhos esticavam junto e sobrava uma
          // faixa branca abaixo do rodape.
          'flex h-full flex-col overflow-hidden rounded-card border-border-strong bg-card transition-colors',
          // Sombra mais alta que a dos widgets: o cartao do atendimento e o
          // que se pega na tela, e a fila inteira precisa se destacar do fundo.
          'shadow-[0_2px_4px_-1px_rgba(15,23,42,0.08),0_8px_16px_-6px_rgba(15,23,42,0.16)] dark:shadow-[0_2px_6px_-1px_rgba(0,0,0,0.6)]',
          canFinish && 'border-amber-500/60 bg-amber-500/5',
          isClosed && 'bg-muted/40',
        )}
      >
        <CardContent className="flex-1 space-y-3 p-4 pb-5 sm:space-y-4 sm:p-5">
          {/* Hora e valor na primeira linha, larguras inteiras: no celular a
              coluna fixa de horario espremia o nome e o servico. */}
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-lg font-semibold tabular-nums">
              {formatTime(appointment.startAt)}
            </span>
            <span className="text-lg font-semibold tabular-nums">
              {formatCurrency(appointment.finalPrice)}
            </span>
          </div>

          <div className="flex items-start gap-3">
            <Avatar name={appointment.customerName} image={appointment.customerImage} />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span
                  className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[appointment.status])}
                  title={APPOINTMENT_STATUS_LABELS[appointment.status]}
                />
                <Link
                  to={SCHEDULE_ROUTES.detail(appointment.id)}
                  className="truncate font-medium hover:underline"
                >
                  {appointment.customerName}
                </Link>
              </div>

              {/* Quem marcou sem escolher pessoa: o balcao ve que pode trocar
                  sem desmarcar nada. */}
              {appointment.anyProfessional ? (
                <Badge className="mt-1 bg-primary/10 text-primary">Qualquer profissional</Badge>
              ) : null}

              {/* A comanda inteira: cada servico com quem atende e quanto
                  custa - e o que o atendente confere na hora de encerrar. */}
              <ul className="mt-0.5 space-y-0.5">
                {appointment.items.map((item) => (
                  <li key={item.id} className="flex items-baseline justify-between gap-2 text-sm">
                    {/* Duas linhas em vez de corte: o nome do servico importa. */}
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      {item.serviceName}
                      <span className="text-muted-foreground">
                        {item.professionalName ? ` - ${item.professionalName}` : ''}
                      </span>
                    </span>
                    {/* Com um servico so, o valor ja esta no topo do cartao. */}
                    {appointment.items.length > 1 ? (
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {formatCurrency(item.finalPrice)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>

              {appointment.checkedInAt ? (
                <p className="text-xs text-muted-foreground">
                  Comecou {formatTime(appointment.checkedInAt)}
                  {appointment.checkedOutAt
                    ? ` - encerrou ${formatTime(appointment.checkedOutAt)}`
                    : ''}
                </p>
              ) : null}
            </div>
          </div>

          <NotesField appointment={appointment} disabled={!canWrite || isClosed} />
        </CardContent>

        {/* Rodape: as acoes valem para tudo que esta acima delas. */}
        {canWrite ? (
          <div className="grid grid-cols-2 items-center gap-3 border-t border-border bg-muted/40 px-4 pb-5 pt-5! sm:gap-3.5 sm:px-5 sm:pb-4 sm:pt-4! [&_a]:rounded-card [&_button]:rounded-card">
            {/* A ordem desce da acao principal para a de apoio: comecar o
                atendimento, empurrar para outro dia, fechar como desistencia
                e, por ultimo, so olhar o cadastro. */}
            {canStart ? (
              <>
                <Button
                  variant="outline"
                  className="h-11 w-full justify-center gap-1.5 border-primary/40 bg-transparent text-sm font-medium text-primary hover:bg-primary/10 sm:h-10"
                  disabled={action.isPending}
                  onClick={() => setConfirm({ mode: 'checkIn', appointment })}
                >
                  <LogIn className="h-4 w-4" />
                  Check-in
                </Button>

                <Button
                  variant="outline"
                  asChild
                  className="h-11 w-full justify-center gap-1.5 border-sky-200 bg-transparent text-sm text-sky-700 hover:bg-sky-50 sm:h-10 dark:border-sky-900 dark:text-sky-300 dark:hover:bg-sky-950"
                >
                  <Link
                    to={SCHEDULE_ROUTES.detail(appointment.id)}
                    state={{ from: SCHEDULE_ROUTES.SERVICE_DESK, reschedule: true }}
                  >
                    <CalendarDays className="h-4 w-4" />
                    Reagendar
                  </Link>
                </Button>

                <Button
                  variant="outline"
                  aria-label="Marcar desistencia"
                  className="h-11 w-full justify-center gap-1.5 border-rose-200 bg-transparent px-2 text-sm text-rose-700 hover:bg-rose-50 sm:h-10 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950"
                  onClick={() => setConfirm({ mode: 'noShow', appointment })}
                >
                  <UserX className="h-4 w-4" />
                  Desistencia
                </Button>
              </>
            ) : canFinish ? (
              <Button
                variant="outline"
                className="h-11 w-full justify-center gap-1.5 border-emerald-300 bg-transparent font-medium text-emerald-700 hover:bg-emerald-50 sm:h-10 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950"
                disabled={action.isPending}
                // Encerrar abre o pagamento: e o momento em que o dinheiro
                // entra e os recebiveis nascem.
                onClick={() => setPendingPayment(appointment)}
              >
                <LogOut className="h-4 w-4" />
                Encerrar
              </Button>
            ) : null}

            {/* Enquanto o atendimento nao fechou da para mexer no que foi
                combinado: servico, horario, desconto e cupom. Antes do
                check-in isso e o proprio Reagendar, ali em cima. */}
            {!isClosed && !canStart ? (
              <Button
                variant="outline"
                asChild
                aria-label="Agendamento"
                className="h-11 w-full justify-center gap-1.5 border-sky-200 bg-transparent px-2 text-sm text-sky-700 hover:bg-sky-50 sm:h-10 dark:border-sky-900 dark:text-sky-300 dark:hover:bg-sky-950"
              >
                <Link
                  to={SCHEDULE_ROUTES.detail(appointment.id)}
                  // Fechar a modal volta para ca, nao para a agenda.
                  state={{ from: SCHEDULE_ROUTES.SERVICE_DESK }}
                >
                  <CalendarDays className="h-4 w-4" />
                  Agendamento
                </Link>
              </Button>
            ) : null}

            <Button
              variant="outline"
              aria-label="Cadastro do cliente"
              className="h-11 w-full justify-center gap-1.5 border-border bg-transparent px-2 text-sm text-slate-700 hover:bg-slate-100 sm:h-10 dark:text-slate-200"
              onClick={() => setEditingCustomer(appointment)}
            >
              <IdCard className="h-4 w-4" />
              Cadastro
            </Button>

            {/* Sem acao possivel, o rodape diz so em que pe esta o horario. */}
            {!canStart && !canFinish ? (
              <span className="self-center text-right text-xs text-muted-foreground">
                {APPOINTMENT_STATUS_LABELS[appointment.status]}
              </span>
            ) : null}
          </div>
        ) : null}
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <OperationTabs current="desk" />

      {/* No celular as abas ja dizem em que tela se esta: repetir o titulo so
          empurrava a fila para baixo. */}
      <div className="hidden sm:block">
        <PageHeader
          icon={<ClipboardCheck />}
          title="Checkin de atendimentos"
          description="A fila do dia, do check-in ao encerramento."
          actions={
            <>
              {/* As duas telas olham a mesma operacao: a agenda pelo horario, a
                fila pelo andamento. A troca fica sempre no mesmo lugar. */}
              <Button asChild className="hidden sm:inline-flex">
                {/* Fecha o submenu junto: a nova tela ja diz onde se esta. */}
                <Link
                  to={SCHEDULE_ROUTES.AGENDA}
                  onClick={() => closeAfterNavigate(SCHEDULE_ROUTES.AGENDA)}
                >
                  <CalendarRange className="h-4 w-4" />
                  Ver agenda
                </Link>
              </Button>
            </>
          }
        />
      </div>

      {/* Barra do dia no mesmo formato da agenda: "Hoje" volta ao comeco e o
          periodo fica entre as setas que o mudam. */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="h-11 shrink-0 rounded-full border-border px-5 text-base shadow-none"
          onClick={() => {
            setFrom(toDateInput(today));
            setTo(toDateInput(today));
          }}
        >
          Hoje
        </Button>

        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-14 w-14 shrink-0 [&_svg]:size-8"
            aria-label="Dia anterior"
            onClick={() => moveDay(-1)}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          {/* Um passo menor no celular pequeno: com as setas grandes ao lado,
              em 360px o texto virava "Quarta, 05...". */}
          <p className="min-w-0 flex-1 truncate text-center text-lg font-medium first-letter:uppercase sm:text-xl lg:flex-none lg:px-2 lg:text-left lg:text-2xl">
            {isSingleDay ? (
              <>
                <span className="sm:hidden">{shortDayLabel(fromDateInput(from))}</span>
                <span className="hidden sm:inline">{longDayLabel(fromDateInput(from))}</span>
              </>
            ) : (
              `${from.split('-').reverse().join('/')} ate ${to.split('-').reverse().join('/')}`
            )}
          </p>

          <Button
            variant="ghost"
            size="icon"
            className="h-14 w-14 shrink-0 [&_svg]:size-8"
            aria-label="Proximo dia"
            onClick={() => moveDay(1)}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Resumo do periodo: o que falta atender, o que esta em andamento, o
          que fechou e quem nao veio - os numeros que o balcao olha entre um
          cliente e outro. Fundo branco e icone colorido para nao sumirem no
          cinza. */}
      {appointments.length > 0 ? (
        // Dois por linha no celular, tres do tablet em diante e a fileira
        // inteira no monitor largo: o tablet tinha espaco para tres e ficava
        // com duas colunas gordas.
        // No celular os contadores nao aparecem: as abas logo abaixo trazem os
        // mesmos numeros, e os seis cartoes empurravam a fila para fora da
        // primeira tela.
        <CardGrid sm={3} xl={6} className="hidden sm:grid">
          {summary.map((item) => (
            <div
              key={item.label}
              className={cn(
                // A mesma elevacao dos widgets do painel: sao a mesma coisa,
                // um numero do dia, e nao faz sentido terem desenhos diferentes.
                'flex items-center gap-2 rounded-card border border-border-strong bg-card px-3 py-3 sm:gap-3 sm:px-4',
                'shadow-[0_1px_2px_0_rgba(15,23,42,0.05),0_2px_6px_-2px_rgba(15,23,42,0.10)] dark:bg-white/[0.06] dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.5)]',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full sm:h-11 sm:w-11',
                  item.tone,
                )}
              >
                {item.icon}
              </span>
              <div className="min-w-0">
                <p className="truncate text-xl font-semibold tabular-nums sm:text-2xl">
                  {item.value}
                </p>
                {/* Sem truncate: "EM ANDAME..." nao diz nada. Em tela de 360px
                    o rotulo quebra em duas linhas e continua legivel; na
                    fileira de seis o espacamento entre letras sai de cena e
                    tudo volta a caber em uma linha so. */}
                <p className="text-xs leading-tight tracking-wide text-muted-foreground xl:tracking-normal">
                  {item.label}
                </p>
              </div>
            </div>
          ))}
        </CardGrid>
      ) : null}

      {action.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(action.error)}</p>
      ) : null}

      {/* Abas coladas na lista: elas dizem o que esta logo abaixo. */}
      {/* Todas as abas a vista: em carrossel as ultimas ficavam escondidas e
          ninguem descobria que existiam. */}
      {/* A faixa de status respira dos dois lados: colada nos numeros acima e
          nos cartoes abaixo, as tres coisas viravam um bloco so. */}
      <div className="flex flex-wrap gap-2 pb-2 pt-3 sm:pb-3 sm:pt-4">
        {TABS.map((entry) => {
          const items = byTab.get(entry.key) ?? [];
          const active = tab === entry.key;

          return (
            <button
              key={entry.key}
              type="button"
              onClick={() => setTab(entry.key)}
              className={cn(
                // Pilula, ao contrario dos cartoes: aba se aperta, cartao se
                // le. Do cartao ela pega so o contorno e a sombra curta.
                'flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-sm transition-colors',
                'shadow-[0_1px_2px_0_rgba(15,23,42,0.05),0_2px_6px_-2px_rgba(15,23,42,0.10)] dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.5)]',
                active
                  ? 'border-primary bg-primary/10 font-medium text-primary'
                  : 'border-border-strong bg-card text-slate-600 hover:bg-muted dark:text-slate-300',
              )}
            >
              <entry.icon className="h-4 w-4" />
              {entry.label}
              <span
                className={cn(
                  'rounded-full px-1.5 text-xs tabular-nums',
                  active ? 'bg-primary/15' : 'bg-muted',
                )}
              >
                {items.length}
              </span>
            </button>
          );
        })}

        {/* Fecha a fila de abas: o que nao e status vira busca avancada, o
            mesmo nome que o resto do sistema usa. */}
        <button
          type="button"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((open) => !open)}
          className={cn(
            'flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-sm transition-colors',
            'shadow-[0_1px_2px_0_rgba(15,23,42,0.05),0_2px_6px_-2px_rgba(15,23,42,0.10)] dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.5)]',
            showFilters
              ? 'border-primary bg-primary/10 font-medium text-primary'
              : 'border-border-strong bg-card text-slate-600 hover:bg-muted dark:text-slate-300',
          )}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Busca avancada
        </button>
      </div>

      {showFilters ? (
        // O mesmo cartao da fila: canto quase reto e a sombra que o levanta.
        <Card
          variant="flush"
          className="border-border-strong shadow-[0_1px_2px_0_rgba(15,23,42,0.05),0_2px_6px_-2px_rgba(15,23,42,0.10)] sm:rounded-card dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.5)]"
        >
          <CardContent className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            <div className="flex items-center gap-2">
              <Input
                type="date"
                aria-label="De"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
              <span className="text-sm text-muted-foreground">a</span>
              <Input
                type="date"
                aria-label="Ate"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>

            <Select
              aria-label="Profissional"
              value={professionalId}
              onChange={(event) => setProfessionalId(event.target.value)}
            >
              <option value="">Todos os profissionais</option>
              {(professionals.data?.data ?? []).map((professional) => (
                <option key={professional.id} value={professional.id}>
                  {professional.name}
                </option>
              ))}
            </Select>

            <Autocomplete
              placeholder="Filtrar por cliente"
              variant="filter"
              url="/api/v1/customers"
              value={customer}
              onChange={setCustomer}
            />

            {units.length > 1 ? (
              <Select
                aria-label="Unidade"
                value={unitId}
                onChange={(event) => setUnitId(event.target.value)}
              >
                <option value="">Todas as unidades</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {agenda.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : visible.length > 0 ? (
        // Tres por linha quando a tela comporta, duas no meio do caminho e uma
        // no celular, com o polegar alcancando o rodape de cada cartao.
        // Quatro colunas no monitor grande: em Full HD tres cartoes deixavam
        // metade da fila fora da tela com espaco sobrando ao lado.
        // Mais folga entre as fileiras que entre as colunas, e um respiro no
        // fim: a sombra do cartao precisa de ar embaixo para nao parecer
        // grudada na fileira seguinte.
        <CardGrid base={1} sm={2} xl={3} xxl={4} className="gap-y-6 pb-6 sm:gap-y-7">
          {visible.map(card)}
        </CardGrid>
      ) : (
        <Card variant="flush">
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              Nada em {TABS.find((entry) => entry.key === tab)?.label.toLowerCase()} neste periodo.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Uma modal para as duas acoes: o texto muda com o modo. */}
      <ConfirmDialog
        open={confirm !== null}
        title={confirm ? CONFIRM_COPY[confirm.mode].title : ''}
        message={
          confirm
            ? `${confirm.appointment.customerName} - ${confirm.appointment.serviceSummary}. ${CONFIRM_COPY[confirm.mode].message}`
            : ''
        }
        confirmLabel={confirm ? CONFIRM_COPY[confirm.mode].label : ''}
        confirmVariant={confirm?.mode === 'checkIn' ? 'default' : 'destructive'}
        loading={action.isPending}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm) action.mutate({ id: confirm.appointment.id, action: confirm.mode });
          setConfirm(null);
        }}
      />

      <PaymentSheet
        open={pendingPayment !== null}
        total={pendingPayment?.finalPrice ?? 0}
        title={`${pendingPayment?.customerName ?? ''} - ${pendingPayment?.serviceSummary ?? ''}`}
        loading={action.isPending}
        error={action.error}
        onClose={() => setPendingPayment(null)}
        onConfirm={(payments) => {
          if (!pendingPayment) return;

          action.mutate(
            { id: pendingPayment.id, action: 'checkOut', payments },
            { onSuccess: () => setPendingPayment(null) },
          );
        }}
      />

      {/* O cadastro do cliente abre por cima da fila: corrigir um telefone nao
          deve tirar o atendente do lugar. */}
      {editingCustomer ? (
        <Modal
          open
          onClose={() => setEditingCustomer(null)}
          title="Cadastro do cliente"
          subtitle={editingCustomer.customerName}
          actions={[
            { label: 'Fechar', variant: 'ghost', onClick: () => setEditingCustomer(null) },
            { label: 'Salvar', type: 'submit', formId: 'desk-customer-form' },
          ]}
        >
          <CustomerForm
            id={editingCustomer.customerId}
            formId="desk-customer-form"
            hideSubmit
            onSaved={() => setEditingCustomer(null)}
          />
        </Modal>
      ) : null}
    </div>
  );
}
